/**
 * フォームバックアップ復元確認モーダル
 *
 * ページフォームなど、状態管理でモーダル表示を制御したい場合に使用します。
 * モーダルフォームでは useFormAutoBackup の showRestoreConfirm() を使用してください。
 *
 * @example
 * ```tsx
 * const { hasBackup, backupData, restoreBackup, clearBackup } = useFormAutoBackup({...});
 * const [showModal, setShowModal] = useState(false);
 *
 * useEffect(() => {
 *   if (hasBackup) setShowModal(true);
 * }, [hasBackup]);
 *
 * return (
 *   <>
 *     <FormBackupRestoreModal
 *       open={showModal}
 *       savedAt={backupData?.savedAt ?? 0}
 *       onRestore={() => {
 *         restoreBackup();
 *         setShowModal(false);
 *       }}
 *       onDiscard={() => {
 *         clearBackup();
 *         setShowModal(false);
 *       }}
 *     />
 *     <Form>...</Form>
 *   </>
 * );
 * ```
 */

import { formatBackupTime } from '../../utils/formBackup';
import { withDevModalFilePath } from './withDevModalFilePath';

const { Modal } = withDevModalFilePath('client/src/components/common/FormBackupRestoreModal.tsx');

export interface FormBackupRestoreModalProps {
  /** モーダル表示フラグ */
  open: boolean;
  /** バックアップ保存日時（Unix timestamp） */
  savedAt: number;
  /** 復元ボタン押下時のコールバック */
  onRestore: () => void;
  /** 破棄ボタン押下時のコールバック */
  onDiscard: () => void;
  /** モーダルタイトル */
  title?: string;
  /** 復元ボタンテキスト */
  restoreText?: string;
  /** 破棄ボタンテキスト */
  discardText?: string;
}

export function FormBackupRestoreModal({
  open,
  savedAt,
  onRestore,
  onDiscard,
  title = '入力途中のデータがあります',
  restoreText = 'はい、復元する',
  discardText = 'いいえ、新規入力',
}: FormBackupRestoreModalProps): JSX.Element {
  return (
    <Modal
      open={open}
      title={title}
      okText={restoreText}
      cancelText={discardText}
      onOk={onRestore}
      onCancel={onDiscard}
      closable={false}
      maskClosable={false}
    >
      <p>
        {savedAt > 0
          ? `${formatBackupTime(savedAt)}に保存されたデータがあります。復元しますか？`
          : '保存されたデータがあります。復元しますか？'}
      </p>
    </Modal>
  );
}

export default FormBackupRestoreModal;
